import React, { useState, useEffect } from 'react';
import { Power, MessageSquare, AlertOctagon, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const MaintenancePanel = () => {
  const [settings, setSettings] = useState({
    maintenance_mode: 'false',
    maintenance_message: ''
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/system/settings`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMaintenance = async () => {
    setUpdating(true);
    const targetState = settings.maintenance_mode === 'true' ? 'false' : 'true';
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/system/settings`, {
        key: 'maintenance_mode',
        value: targetState
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setSettings(prev => ({ ...prev, maintenance_mode: targetState }));
        alert(`Maintenance Mode turned ${targetState === 'true' ? 'ON' : 'OFF'}`);
      }
    } catch (err) {
      alert("Failed to toggle mode");
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateMessage = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/system/settings`, {
        key: 'maintenance_message',
        value: settings.maintenance_message
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        alert("Maintenance warning template updated successfully!");
      }
    } catch (err) {
      alert("Failed to update message template");
    } finally {
      setUpdating(false);
    }
  };

  const isActive = settings.maintenance_mode === 'true';

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-300">
      <div>
        <h2 className="text-3xl font-extrabold text-slate-800">System Maintenance Mode</h2>
        <p className="text-slate-500 mt-1">Temporarily block standard API access for non-administrator users during system upgrades.</p>
      </div>

      {loading ? (
        <div className="bg-white rounded-3xl border border-slate-100 p-20 text-center shadow-xl">
          <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Checking maintenance variables...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Master Trigger Card */}
          <div className="md:col-span-1 bg-white p-8 rounded-3xl border border-slate-100 shadow-xl flex flex-col justify-between items-center text-center">
            <div className={`p-5 rounded-full ${isActive ? 'bg-red-50 text-red-600 animate-pulse' : 'bg-slate-50 text-slate-400'}`}>
              <Power size={48} />
            </div>

            <div className="space-y-2 mt-6">
              <h3 className="text-xl font-bold text-slate-800">Master Switch</h3>
              <p className="text-xs text-slate-400">
                {isActive 
                  ? "App is currently BLOCKED for users. Administrators can still enter." 
                  : "App is operational. Users can join live sessions."}
              </p>
            </div>

            <button
              onClick={handleToggleMaintenance}
              disabled={updating}
              className={`w-full py-4 rounded-2xl font-bold mt-8 shadow-lg active:scale-95 transition ${
                isActive 
                  ? 'bg-red-600 hover:bg-red-700 text-white shadow-red-200' 
                  : 'bg-slate-900 hover:bg-slate-800 text-white shadow-slate-200'
              }`}
            >
              {updating ? "Switching..." : isActive ? "Turn Off Maintenance" : "Activate Maintenance"}
            </button>
          </div>

          {/* Settings / Message Template Card */}
          <div className="md:col-span-2 bg-white p-8 rounded-3xl border border-slate-100 shadow-xl flex flex-col justify-between">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-4">
              <MessageSquare size={20} className="text-pink-600" /> Custom Maintenance Message
            </h3>

            <form onSubmit={handleUpdateMessage} className="space-y-4 flex-1 flex flex-col justify-between">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Warning Message shown to users</label>
                <textarea
                  required
                  placeholder="Specify maintenance details..."
                  className="w-full mt-2 p-4 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 font-semibold text-slate-700 h-32"
                  value={settings.maintenance_message}
                  onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-slate-50">
                <button
                  type="submit"
                  disabled={updating}
                  className="px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl active:scale-95 transition"
                >
                  Save Message
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenancePanel;
